import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Tier 3 — Security (Feb 2026): Block screenshots/recordings for field staff.
// Roles affected: salesman, manager. (Management/Super Admin/Retailer/Wholesaler are exempt.)
//
// IMPLEMENTATION
//   • Native (Capacitor APK): @capacitor-community/privacy-screen → Android FLAG_SECURE.
//     Screenshots/recordings render as black, app-switcher preview blurred.
//   • Web (PWA in browser): browsers don't expose APIs to block screenshots.
//     We only show a one-time advisory toast — true protection requires the APK build.
//
// LIMITATIONS (per user requirement Note):
//   • Cannot stop external camera recording.
//   • Rooted devices may bypass FLAG_SECURE.
//   These are accepted constraints; backend authorization remains the source of truth.

const PROTECTED_ROLES = ["salesman", "manager"];
const TOAST_KEY = "gtroyal_screenshot_toast_shown";

let pluginRef = null;
let pluginLoadAttempted = false;

const loadPlugin = async () => {
  if (pluginLoadAttempted) return pluginRef;
  pluginLoadAttempted = true;
  try {
    const mod = await import("@capacitor-community/privacy-screen");
    pluginRef = mod.PrivacyScreen || null;
  } catch (_) {
    pluginRef = null;
  }
  return pluginRef;
};

const isNativeRuntime = () => {
  // Capacitor sets `window.Capacitor.isNativePlatform()` on Android/iOS APK
  try {
    return typeof window !== "undefined" && window.Capacitor && typeof window.Capacitor.isNativePlatform === "function" && window.Capacitor.isNativePlatform();
  } catch (_) {
    return false;
  }
};

export default function useScreenshotProtection() {
  const { user } = useAuth();
  const { lang } = useLang();
  const lastStateRef = useRef(null);

  useEffect(() => {
    const role = user?.role;
    const shouldProtect = !!role && PROTECTED_ROLES.includes(role);

    // Avoid re-firing the plugin on every re-render.
    if (lastStateRef.current === shouldProtect) return;
    lastStateRef.current = shouldProtect;

    // Show one-time advisory toast for protected roles (web OR native)
    if (shouldProtect && !sessionStorage.getItem(TOAST_KEY)) {
      sessionStorage.setItem(TOAST_KEY, "1");
      toast(lang === "ta"
          ? "பாதுகாப்பு காரணமாக ஸ்கிரீன்ஷாட் தடைசெய்யப்பட்டுள்ளது"
          : "Screenshots are disabled for security reasons",
        { duration: 3500, icon: "🔒" }
      );
    }

    // Native enforcement (Android FLAG_SECURE via Capacitor plugin)
    if (!isNativeRuntime()) return;
    let cancelled = false;
    (async () => {
      const plugin = await loadPlugin();
      if (cancelled || !plugin) return;
      try {
        if (shouldProtect) {
          await plugin.enable({ android: { dimBackground: true } });
        } else {
          await plugin.disable();
        }
      } catch (_) {
        // Swallow — protection is best-effort, never breaks navigation.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role, lang]);
}
